import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import useModalState from '@/hooks/pure/useModalState';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import { IMPORT_STATE, type StampedInfo } from '../types';
import StampedTokenModal from './StampedTokenModal';
import { useStamped } from './use-stamped';

export const StampedItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const {
    stampedWorkspace,
    disconnectStampedMutation,
    requestImportMutation,
    setStampedWorkspace,
  } = useStamped(!isConnector);

  const tokenModalState = useModalState();
  const importModalState = useModalState<{ isNew: boolean }>();

  const handleConnectComplete = (_workspace: StampedInfo) => {
    if (!currWorkspace) return;

    setStampedWorkspace(_workspace);
    tokenModalState.close();
  };

  const handleDisconnect = () => {
    disconnectStampedMutation();
  };

  const handleImport = (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!stampedWorkspace}
        source={SOURCE_TYPE.STAMPED}
        description="Import reviews as feedback into Syncly."
        connectedDisplay={'Stamped'}
        info={stampedWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => tokenModalState.open()}
        additionalActions={(workspace) =>
          workspace.import_state === IMPORT_STATE.IMPORTING ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <Typo type="body2" className="text-primary">
                Importing
              </Typo>
            </div>
          ) : (
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({ modalState: { isNew: false } })
              }
            >
              One-time import
            </Button>
          )
        }
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import Reviews'}
          subTitle={'Stamped'}
          historyList={stampedWorkspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImport({ start_date, end_date })
          }
        />
      </Modal>
      <Modal
        status={tokenModalState.status}
        closeModal={tokenModalState.close}
        useBackgroundClose
      >
        <StampedTokenModal onConnectComplete={handleConnectComplete} />
      </Modal>
    </>
  );
};
